import type { Bilingual } from '@/shared/types/api';

/**
 * Quick Find — the response the three search routes return (CONTRACT §2).
 *
 * One route per portal, each behind that portal's own guard:
 *
 *   tenant    GET /api/v1/search           (signed in, MFA satisfied)
 *   vendor    GET /api/v1/vendor/search    (supplier session)
 *   platform  GET /api/v1/platform/search  (operator session, MFA satisfied)
 *
 * The server decides every family the person may read, BEFORE it queries: a
 * family they may not read is absent, never "empty". There are no totals or
 * counts anywhere — only whether more exist. A family that failed at runtime
 * comes back `unavailable` with no items: the partial state, which the UI
 * names and never presents as "no results".
 *
 * Titles and contexts are bilingual pairs and statuses are stable codes, so
 * the client renders them in the reader's language without asking again.
 */
export type SearchPortal = 'tenant' | 'vendor' | 'platform';

/**
 * The families each portal can return, in the order the overlay shows them.
 *
 * The supplier's `invoices` (BC-S3) are its own invoices on its own orders,
 * answered only where the buyer's subscription includes invoices: the server
 * leaves the family out otherwise, so the shell never names it in the entry's
 * scope (it cannot know), and shows the group only when it is answered.
 */
export const PORTAL_FAMILIES = {
  tenant: ['catalogue_items', 'requests', 'purchase_orders', 'rfqs', 'suppliers'],
  vendor: ['orders', 'rfq_invitations', 'invoices'],
  platform: ['organisations'],
} as const satisfies Record<SearchPortal, readonly string[]>;

export type TenantFamily = (typeof PORTAL_FAMILIES)['tenant'][number];
export type VendorFamily = (typeof PORTAL_FAMILIES)['vendor'][number];
export type PlatformFamily = (typeof PORTAL_FAMILIES)['platform'][number];
export type SearchFamily = TenantFamily | VendorFamily | PlatformFamily;

/** A record page, by a safe route KEY the client maps to a path (`routes.ts`). */
export interface QuickFindDestination {
  route: string;
  params: Record<string, string>;
}

/** "See all in <list>": that list's own route key and its own `search`. */
export interface QuickFindSeeAll {
  route: string;
  query: { search: string };
}

export interface QuickFindItem {
  /**
   * The record's public id. Its destination names the page to open, which
   * may be another record's (a supplier invoice opens its order, BC-S3).
   */
  id: string;
  type: string;
  /** Single-language user text (an RFQ title) arrives as the same text twice. */
  title: Bilingual;
  /** A number or code (PO number, item code), or null. */
  code: string | null;
  /** One line of permitted context — a category, a purchasing node, a buyer. */
  context: Bilingual | null;
  /** A stable status code, rendered through the client's own dictionary. */
  status: string | null;
  destination: QuickFindDestination;
}

export interface QuickFindGroup {
  /** The family key (`catalogue_items`, `orders`, `organisations`…). */
  type: string;
  /** `unavailable`: this family failed on the server; its items are empty. */
  status: 'ok' | 'unavailable';
  items: QuickFindItem[];
  has_more: boolean;
  see_all: QuickFindSeeAll | null;
}

export interface QuickFindResult {
  /** The term as the server normalised it. */
  q: string;
  groups: QuickFindGroup[];
}

export interface QuickFindRequest {
  q: string;
  /** A subset of the portal's family keys; all of them when absent. */
  types?: readonly string[];
  /** Per family, 1–10; the server's default (5) when absent. */
  limit?: number;
  signal?: AbortSignal;
}
